import React, { useMemo, useState } from "react";
import { Alert, Box, CircularProgress, Paper, Stack, Typography } from "@mui/material";
import { GoogleLogin } from "@react-oauth/google";
import { useParams } from "react-router-dom";
import api from "../../services/api";
import AppleSignInButton from "./AppleSignInButton";

function postToNative(message) {
  if (typeof window === "undefined") return false;
  if (!window.ReactNativeWebView?.postMessage) return false;
  window.ReactNativeWebView.postMessage(JSON.stringify(message));
  return true;
}

export default function MobileSocialAuthBridge() {
  const { provider } = useParams();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [providers, setProviders] = useState({
    apple: false,
    google: true,
    apple_client_id: "",
    apple_redirect_uri: "",
  });

  const normalizedProvider = useMemo(
    () => String(provider || "").trim().toLowerCase(),
    [provider]
  );

  const desiredPlan = useMemo(() => {
    const query = new URLSearchParams(window.location.search || "");
    return (query.get("desired_plan") || "free").trim().toLowerCase() || "free";
  }, []);

  React.useEffect(() => {
    let active = true;
    api.users
      .getAuthProviders()
      .then((response) => {
        if (!active) return;
        const payload = response?.data || {};
        setProviders({
          apple: Boolean(payload.apple),
          google: payload.google !== false,
          apple_client_id: String(payload.apple_client_id || "").trim(),
          apple_redirect_uri: String(payload.apple_redirect_uri || "").trim(),
        });
      })
      .catch(() => {
        if (!active) return;
        setProviders({
          apple: false,
          google: true,
          apple_client_id: "",
          apple_redirect_uri: "",
        });
      });
    return () => {
      active = false;
    };
  }, []);

  const handleSuccess = async (payloadPromise) => {
    setLoading(true);
    setError("");
    try {
      const payload = await payloadPromise;
      if (!postToNative({ type: "auth-success", payload: payload?.data || payload })) {
        setError("This auth bridge must be opened from the mobile app.");
      }
    } catch (nextError) {
      const message =
        nextError?.response?.data?.error ||
        nextError?.message ||
        "Authentication failed.";
      setError(message);
      postToNative({ type: "auth-error", error: message });
    } finally {
      setLoading(false);
    }
  };

  const renderProvider = () => {
    if (normalizedProvider === "google") {
      return (
        <Box
          sx={{
            width: "100%",
            "& iframe": { width: "100% !important" },
            "& > div": { width: "100% !important" },
            "& > div > div": { width: "100% !important" },
          }}
        >
          <GoogleLogin
            onSuccess={(credentialResponse) =>
              handleSuccess(
                api.users.googleLogin({
                  token: credentialResponse.credential,
                  desired_plan: desiredPlan,
                })
              )
            }
            onError={() => setError("Google Sign-In failed.")}
            useOneTap={false}
          />
        </Box>
      );
    }

    if (normalizedProvider === "apple") {
      if (!providers.apple) {
        return <Alert severity="error">This provider is currently unavailable.</Alert>;
      }
      return (
        <AppleSignInButton
          label="Continue with Apple"
          onSuccess={({ idToken, user }) =>
            handleSuccess(
              api.users.appleLogin({
                id_token: idToken,
                desired_plan: desiredPlan,
                user,
                email: user?.email || null,
                is_private_email: false,
              })
            )
          }
          onError={(nextError) =>
            setError(nextError?.message || "Apple Sign-In failed.")
          }
          clientId={providers.apple_client_id}
          redirectURI={providers.apple_redirect_uri}
        />
      );
    }

    return <Alert severity="error">Unsupported auth provider.</Alert>;
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        background:
          "radial-gradient(circle at top, rgba(73,188,144,0.14), transparent 40%), #fbfaf7",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 420,
          borderRadius: 5,
          p: 4,
          border: "1px solid rgba(18,25,20,0.08)",
          boxShadow: "0 20px 60px rgba(18,25,20,0.08)",
        }}
      >
        <Stack spacing={2.5}>
          <Box>
            <Typography variant="overline" sx={{ letterSpacing: 1.8, color: "text.secondary" }}>
              InCooking Mobile
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5 }}>
              Continue securely
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Use the same InCooking account across web and mobile.
            </Typography>
          </Box>

          {error ? <Alert severity="error">{error}</Alert> : null}
          {renderProvider()}
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
              <CircularProgress size={22} />
            </Box>
          ) : null}
        </Stack>
      </Paper>
    </Box>
  );
}
